import type { z } from "zod";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Read an element's props against its catalog schema, salvaging what it can.
 *
 * The renderer does not validate props (partial and streaming specs must still
 * render), and an App's data can drift from what its view expected. So a prop
 * that fails its schema is dropped individually instead of failing the whole
 * element: a Table whose `emptyText` is a number still renders its rows.
 * Every key comes back optional, and components supply their own defaults.
 */
export function readProps<S extends z.ZodObject>(
  schema: S,
  raw: unknown,
): Partial<z.infer<S>> {
  const whole = schema.safeParse(raw);
  if (whole.success) return whole.data;
  if (!isRecord(raw)) return {};
  const salvaged: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(schema.shape)) {
    if (raw[key] === undefined) continue;
    const parsed = field.safeParse(raw[key]);
    if (parsed.success) salvaged[key] = parsed.data;
  }
  // Each kept key passed its own field schema, so the object is a valid
  // partial of the inferred shape; TypeScript cannot follow the per-key loop.
  return salvaged as Partial<z.infer<S>>;
}
